import { Action } from './action';
import { ActionType, Loaded } from './action-types';
import { State } from './AdminProvider';
import { RequiredField } from 'common/types/admin-page.type';

const reducer = <T extends RequiredField>(state: State<T>, action: ActionType<T>) => {
  const { type } = action;

  switch (type) {
    case Action.LOADING:
      return handleLoading(state);

    case Action.LOADED:
      return handleLoaded(state, action.payload);

    case Action.SELECT_ALL:
      return handleSelectAll(state);

    case Action.DESELECT_ALL:
      return handleDeselectAll(state);

    case Action.TOGGLE_SELECT:
      return handleToggleSelect(state, action.payload);

    case Action.SET_TO_EDIT:
      return handleSetToEdit(state, action.payload);

    case Action.SET_TO_DELETE:
      return handleSetToDelete(state, action.payload);

    case Action.SET_FILTER:
      return handleSetFilter(state, action.payload);

    case Action.DELETE_ROWS:
      return handleDeleteRows(state, action.payload);

    case Action.UPDATE_ROW:
      return handleUpdateRow(state, action.payload);

    case Action.ADD_ROW:
      return handleAddRow(state, action.payload);

    case Action.SET_SORT:
      return handleSetSort(state, action.payload);

    default:
      throw new Error(`Unhandled action type: ${type}`);
  }
};

function handleLoading<T extends RequiredField>(state: State<T>): State<T> {
  return { ...state, isLoading: true };
}

function handleLoaded<T extends RequiredField>(state: State<T>, response: Loaded<T>['payload']): State<T> {
  const { rows, pagination } = response;
  return { ...state, isLoading: false, rows, params: { ...state.params, pagination } };
}

function handleSelectAll<T extends RequiredField>(state: State<T>): State<T> {
  const rows = state.rows.map((row) => ({ ...row, isSelected: true }));
  return { ...state, rows };
}

function handleDeselectAll<T extends RequiredField>(state: State<T>): State<T> {
  const rows = state.rows.map((row) => ({ ...row, isSelected: false }));
  return { ...state, rows };
}

function handleToggleSelect<T extends RequiredField>(state: State<T>, row: State<T>['rows'][0]): State<T> {
  const rows = state.rows.map((currRow) => {
    if (currRow.id !== row.id) return currRow;
    return { ...currRow, isSelected: !currRow.isSelected };
  });
  return { ...state, rows };
}

function handleSetToEdit<T extends RequiredField>(state: State<T>, row: State<T>['rowToEdit']): State<T> {
  return { ...state, rowToEdit: row };
}

function handleSetToDelete<T extends RequiredField>(state: State<T>, rows: State<T>['rowsToDelete']): State<T> {
  return { ...state, rowsToDelete: rows };
}

function handleSetFilter<T extends RequiredField>(state: State<T>, filter: State<T>['params']['filter']): State<T> {
  return { ...state, params: { ...state.params, filter } };
}

function handleDeleteRows<T extends RequiredField>(state: State<T>, rows: State<T>['rows']): State<T> {
  const ids = rows.map((row) => row.id);
  const newRows = state.rows.filter((row) => !ids.includes(row.id));
  return { ...state, rows: newRows };
}

function handleUpdateRow<T extends RequiredField>(state: State<T>, row: State<T>['rows'][0]): State<T> {
  const rows = state.rows.map((currRow) => (currRow.id === row.id ? row : currRow));
  return { ...state, rows };
}

function handleAddRow<T extends RequiredField>(state: State<T>, row: State<T>['rows'][0]): State<T> {
  return { ...state, rows: [row, ...state.rows] };
}

function handleSetSort<T extends RequiredField>(state: State<T>, sort: State<T>['params']['sort']): State<T> {
  return { ...state, params: { ...state.params, sort } };
}

export default reducer;
